import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { trpc } from "@/lib/trpc";
import { CreateProductSchema, type CreateProductInput, type CustomFeatureItem } from "@/types/activation";
import { zodResolver } from "@hookform/resolvers/zod";
import { PlusCircle } from "lucide-react";
import { ProductFeatureInput, type FormCustomFeatureItem } from "./ProductFeatureInput";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

interface Feature {
  id: string;
  key: string;
  name: string;
  description?: string | null;
}

interface FormValuesWithCustomFeatures {
  name?: string;
  description?: string;
  monthlyPrice?: number;
  yearlyPrice?: number;
  featureIds?: string[];
  customFeatures?: FormCustomFeatureItem[];
  maxBindingsPerCode?: number;
}

export function CreateProductDialog({ onProductCreated }: { onProductCreated?: () => void }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const form = useForm<FormValuesWithCustomFeatures>({
    resolver: zodResolver(CreateProductSchema),
    defaultValues: {
      name: "",
      description: "",
      monthlyPrice: undefined,
      yearlyPrice: undefined,
      featureIds: [],
      customFeatures: [],
      maxBindingsPerCode: 1, // Default to 1 as per schema
    },
  });

  const utils = trpc.useUtils();
  const createProductMutation = trpc.activation.createProduct.useMutation({
    onSuccess: (data) => {
      toast.success(t("admin.activation.products.createSuccess", { name: data.name }));

      // 立即强制重新获取产品列表
      utils.activation.listProducts.invalidate();

      // 添加延时，确保数据更新后再关闭对话框
      setTimeout(() => {
        onProductCreated?.();
        setIsOpen(false);
        form.reset();
      }, 300);
    },
    onError: (error) => {
      toast.error(t("admin.activation.products.createError", { message: error.message }));
    },
  });

  const { data: featuresData, isLoading: isLoadingFeatures } = trpc.activation.listFeatures.useQuery();

  const onSubmit = async (data: FormValuesWithCustomFeatures) => {
    const apiPayload: CreateProductInput = {
      name: data.name || "", // Ensure name is a string, assuming form validation handles non-empty
      description: data.description,
      monthlyPrice: data.monthlyPrice,
      yearlyPrice: data.yearlyPrice,
      featureIds: data.featureIds || [],
      maxBindingsPerCode: data.maxBindingsPerCode,
      // customFeatures will be processed and added below
    };

    // customFeatures is already an array of FormCustomFeatureItem,
    // which is compatible with CustomFeatureItem[] since id is optional.
    if (data.customFeatures && Array.isArray(data.customFeatures)) {
      // Filter out any features that might be incomplete if necessary,
      // though Zod schema validation should handle this.
      apiPayload.customFeatures = data.customFeatures.filter(cf => cf.keyName).map(cf => ({
        id: cf.id, // id is optional in CustomFeatureItemSchema
        keyName: cf.keyName,
        value: cf.value,
        valueType: cf.valueType,
      }));
      if (apiPayload.customFeatures.length === 0) {
        apiPayload.customFeatures = undefined; // or [] depending on schema default and API expectation
      }
    } else {
      apiPayload.customFeatures = undefined;
    }

    console.log("提交创建数据:", apiPayload);

    try {
      createProductMutation.mutate(apiPayload);
    } catch (error) {
      console.error('创建产品失败:', error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button>
          <PlusCircle className="mr-2 h-4 w-4" />
          {t("admin.activation.products.createNew")}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>{t("admin.activation.products.createNewProductTitle")}</DialogTitle>
          <DialogDescription>
            {t("admin.activation.products.createNewProductDesc")}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-medium">{t("admin.activation.products.productName")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("admin.activation.products.productNamePlaceholder")}
                      className="h-10"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-medium">{t("admin.activation.products.productDescription")}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t("admin.activation.products.productDescriptionPlaceholder")}
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* 价格输入字段 */}
            <div className="space-y-4">
              <h3 className="text-base font-medium">{t("admin.activation.products.pricing")}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="monthlyPrice"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("admin.activation.products.monthlyPrice")}</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              className="h-10 pl-7"
                              {...field}
                              onChange={(e) => {
                                const value = e.target.value ? Number.parseFloat(e.target.value) : undefined;
                                field.onChange(value);
                              }}
                              value={field.value ?? ""}
                            />
                          </div>
                        </FormControl>
                        <FormDescription className="text-xs">
                          {t("admin.activation.products.optional")}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="yearlyPrice"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("admin.activation.products.yearlyPrice")}</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              className="h-10 pl-7"
                              {...field}
                              onChange={(e) => {
                                const value = e.target.value ? Number.parseFloat(e.target.value) : undefined;
                                field.onChange(value);
                              }}
                              value={field.value ?? ""}
                            />
                          </div>
                        </FormControl>
                        <FormDescription className="text-xs">
                          {t("admin.activation.products.optional")}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>

            <FormField
              control={form.control}
              name="maxBindingsPerCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-medium">{t("admin.activation.products.maxBindingsPerCode")}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      placeholder={t("admin.activation.products.maxBindingsPerCodePlaceholder")}
                      className="h-10"
                      {...field}
                      onChange={(e) => {
                        const value = e.target.value ? Number.parseInt(e.target.value, 10) : 0;
                        field.onChange(value >= 0 ? value : 0);
                      }}
                      value={field.value ?? 1}
                    />
                  </FormControl>
                  <FormDescription>
                    {t("admin.activation.products.maxBindingsPerCodeDesc")}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Custom Features Input */}
            <FormField
              control={form.control}
              name="customFeatures"
              render={() => (
                <FormItem>
                  <FormLabel className="text-base font-medium">{t("admin.activation.products.features")}</FormLabel>
                  <div className="border rounded-md p-3 bg-card">
                    <ProductFeatureInput
                     control={form.control}
                     register={form.register}
                     errors={form.formState.errors}
                     setValue={form.setValue}
                     getValues={form.getValues}
                   />
                   <FormMessage />
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2 pt-2">
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  {t("common.cancel")}
                </Button>
              </DialogClose>
              <Button type="submit" disabled={createProductMutation.isPending}>
                {createProductMutation.isPending ? t("common.creating") : t("common.create")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}